import React, { useEffect, useState } from 'react';
import {
  Backdrop,
  Card,
  CircularProgress,
  FormControl,
  MenuItem,
  Dialog,
  DialogContent,
  DialogTitle,
  Select,
  Stack,
  Button,
  Tooltip,
  Typography,
  useMediaQuery,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { Helmet } from 'react-helmet-async';
import DynamicTable from 'src/components/shared/DynamicTable';
import Services from 'src/services/Services';
import { useLocalization } from 'src/context/LocalizationContext';
import moment from 'moment';
import FilterViewButton from 'src/components/shared/FilterViewButton';
import CustomFilter from 'src/components/filter/CustomFilter';
import Iconify from 'src/components/iconify';
import { tokenService } from 'src/services';

export default function LockerTrackingSystem() {
  const { language } = useLocalization();

  const [loading, setLoading] = useState(true);
  const [buttonLoading, setButtonloading] = useState(false);
  const [filterModal, setFilterModal] = useState(false);
  const isMobile = useMediaQuery('(max-width:768px)');

  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [lockers, setLockers] = useState([]);

  const [openDialog, setOpenDialog] = useState(false);
  const [selectedLocker, setSelectedLocker] = useState(null);

  const [openPasswordDialog, setOpenPasswordDialog] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [noLocker, setNoLocker] = useState(false);

  const handleModal = () => {
    setFilterModal(true);
  };

  const handleCloseModal = () => {
    setFilterModal(false);
  };

  const handleOpenDialog = (locker) => {
    setSelectedLocker(locker);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleOpenPasswordDialog = () => {
    setOpenPasswordDialog(true);
  };

  const handleClosePasswordDialog = () => {
    setOpenPasswordDialog(false);
    setSelectedLocker(null);
    setShowPassword(false);
    setPassword('');
    getLockers(selectedLocation?.value);
  };

  const handlePasswordRequest = (row) => {
    if (row) {
      setSelectedLocker(row);
    }
    handleCloseDialog();
    handleOpenPasswordDialog();
  };

  const getStatusStyle = (status) => {
    if (status === 'Empty') {
      return {
        backgroundColor: '#ECFDF3',
        color: '#067647',
        border: '1px solid #ABEFC6',
        borderRadius: '12px',
        display: 'inline-flex',
        alignItems: 'center',
        padding: '4px 8px',
      };
    } else {
      return {
        backgroundColor: '#FEF3F2',
        color: '#B42318',
        border: '1px solid #FECDCA',
        borderRadius: '12px',
        display: 'inline-flex',
        alignItems: 'center',
        padding: '4px 8px',
      };
    }
  };

  const getLockerNumberStyle = (status) => {
    return {
      backgroundColor: '#F9FAFB',
      color: status === 'Empty' ? '#067647' : '#B42318',
      border: '1px solid #EAECF0',
      borderRadius: '12px',
      display: 'inline-flex',
      alignItems: 'center',
      padding: '4px 8px',
      gap: '8px',
    };
  };

  const getLockIconStyle = {
    width: '16px',
    height: '16px',
    color: '#A5A4A4',
  };

  let TABLE_HEAD = [
    {
      id: 'lockerNumber',
      label: language.commons.labels.locker_no,
      format: (value, row) => (
        <div style={getLockerNumberStyle(row.statusName)}>
          <Iconify icon="mdi:lock" style={getLockIconStyle} />
          <span>{value}</span>
        </div>
      ),
    },
    {
      id: 'startDate',
      label: language.commons.labels.start_date,
      format: (value) => formatDate(value),
    },
    {
      id: 'endDate',
      label: language.commons.labels.end_date,
      format: (value) => formatDate(value),
    },
    {
      id: 'statusName',
      label: language.commons.status,
      format: (value) => (
        <div style={getStatusStyle(value)}>
          <span
            style={{
              width: '8px',
              height: '8px',
              backgroundColor: value === 'Empty' ? '#17B26A' : '#F04438',
              borderRadius: '50%',
              marginRight: '8px',
            }}
          />
          {value === 'Empty' ? 'Boşta' : 'Dolu'}
        </div>
      ),
    },
    {
      id: 'pointTitle',
      label: language.commons.get_password,
      alignRight: false,
      format: (value, row) => (
        <Typography
          style={{
            padding: '5px',
            border: 'solid 1px #EAECF0',
            backgroundColor: '#F9FAFB',
            color: '#344054',
            fontSize: 14,
            fontWeight: '400',
            cursor: 'pointer',
            borderRadius: 6,
            textAlign: 'center',
            width: 80,
            height: 36,
          }}
          onClick={() => handlePasswordRequest(row)}
        >
          Şifre Al
        </Typography>
      ),
    },
    {
      id: 'action',
      label: language.commons.column.details,
      alignRight: false,
      format: (value, row) => (
        <Tooltip title={language.commons.detail}>
          <Iconify
            style={{
              color: '#98A2B3',
              width: 31,
              height: 31,
              cursor: 'pointer',
              padding: '5px',
              border: 'solid 1px #EAECF0',
              borderRadius: 6,
              backgroundColor: '#F9FAFB',
            }}
            icon="bx:detail"
            onClick={() => handleOpenDialog(row)}
          />
        </Tooltip>
      ),
    },
  ];

  const calculateUsageTime = (start, end) => {
    if (!start || !end) return '-';

    const startMoment = moment(start);
    const endMoment = moment(end);

    if (!startMoment.isValid() || !endMoment.isValid()) return '-';

    const duration = moment.duration(endMoment.diff(startMoment));

    const days = duration.days();
    const hours = duration.hours();
    const minutes = duration.minutes();

    let usageTime = [];
    if (days > 0) {
      usageTime.push(`${days} gün`);
    }
    if (hours > 0) {
      usageTime.push(`${hours} saat`);
    }
    if (minutes > 0 || usageTime.length === 0) {
      usageTime.push(`${minutes} dakika`);
    }

    return usageTime.join(' ');
  };

  const formatDate = (dateString) => {
    const formattedDate = dateString
      ? moment(dateString).local().format('DD.MM.YYYY | HH:mm')
      : '';
    return formattedDate;
  };

  const formatGSM = (gsm) => {
    if (!gsm) return gsm;

    const raw = String(gsm);
    // tüm olmayan rakamları temizle (boşluk, -, (, ) vb.)
    let digits = raw.replace(/\D+/g, '');

    if (!digits) return gsm;

    // 00 ile başlayan uluslararası formatı 90'a indir
    if (digits.startsWith('00')) digits = digits.slice(2);

    // Aşağıdaki varyantları 10 hanelik ulusal numaraya çevir:
    // 90 5xxxxxxxxx  -> 5xxxxxxxxx
    // 0 5xxxxxxxxx   -> 5xxxxxxxxx
    // 5xxxxxxxxx     -> 5xxxxxxxxx
    let national = null;
    if (digits.startsWith('90') && digits.length >= 12) {
      national = digits.slice(-10);
    } else if (digits.startsWith('0') && digits.length >= 11) {
      national = digits.slice(-10);
    } else if (digits.length === 10) {
      national = digits;
    }

    // Geçersizse orijinali döndür
    if (!national || national.length !== 10 || national[0] !== '5') return gsm;

    // Maskeleme: "+905" + (ara x'ler) + son 4 hane
    const prefix = '+905';
    const lastFour = national.slice(-4);
    const hiddenLen = national.length - 1 - 4; // operatörün ilk hanesi (5) + son 4 görünür
    return `${prefix}${'*'.repeat(Math.max(0, hiddenLen))}${lastFour}`;
  };

  const maskName = (fullName) => {
    if (!fullName || typeof fullName !== 'string') return '-';
    const parts = fullName.trim().split(/\s+/).filter(Boolean);
    const maskToken = (tok) => {
      if (!tok) return '';
      const visible = tok.slice(0, 2);
      return `${visible}***`;
    };

    if (parts.length === 1) {
      return maskToken(parts[0]);
    }

    if (parts.length === 2) {
      return `${maskToken(parts[0])} ${maskToken(parts[1])}`;
    }

    return `${maskToken(parts[0])} ${maskToken(parts[parts.length - 1])}`;
  };

  // --- YENİ: email öncelikli iletişim bilgisi ve lockerPass maskeleme
  const getEmailFromLocker = (locker) =>
    locker?.email || locker?.mail || locker?.emailAddress || null;

  const maskLockerPass = (pass) => {
    if (pass === null || pass === undefined) return '-';
    const str = String(pass);
    const last4 = str.slice(-4);
    const masked = '*'.repeat(Math.max(0, str.length - 4)) + last4;
    return masked;
  };

  const getPassword = () => {
    const qrValue = selectedLocker?.qrCode;
    if (selectedLocker?.statusName === 'InUse') {
      setButtonloading(true);
      Services.FinishBox(qrValue)
        .then((finishRes) => {
          if (finishRes?.data?.finish?.isSuccessStatusCode) {
            Services.LockerLoad(qrValue)
              .then((lockerRes) => {
                if (lockerRes?.data?.loadData?.otpCode) {
                  setPassword(lockerRes?.data?.loadData?.otpCode);
                  Services.FinishBox(lockerRes?.data?.loadData?.qrCode)
                    .then((finishRes2) => {
                      if (finishRes2?.data?.finish?.isSuccessStatusCode) {
                        setButtonloading(false);
                        setShowPassword(true);
                      } else {
                        setButtonloading(false);
                      }
                    })
                    .catch(() => {
                      setButtonloading(false);
                    });
                } else {
                  setButtonloading(false);
                }
              })
              .catch(() => {
                setButtonloading(false);
              });
          } else {
            setButtonloading(false);
          }
        })
        .catch(() => {
          setButtonloading(false);
        });
    } else if (selectedLocker?.statusName === 'Empty') {
      setButtonloading(true);
      Services.LockerLoad(qrValue)
        .then((lockerRes) => {
          if (lockerRes?.data?.loadData?.otpCode) {
            setPassword(lockerRes?.data?.loadData?.otpCode);
            Services.FinishBox(lockerRes?.data?.loadData?.qrCode)
              .then((finishRes) => {
                if (finishRes?.data?.finish?.isSuccessStatusCode) {
                  setButtonloading(false);
                  setShowPassword(true);
                } else {
                  setButtonloading(false);
                }
              })
              .catch(() => {
                setButtonloading(false);
              });
          } else {
            setButtonloading(false);
          }
        })
        .catch(() => {
          setButtonloading(false);
        });
    }
  };

  const getLockers = (code) => {
    setLoading(true);
    Services.GetLockers(code)
      .then((res) => {
        if (res.data) {
          setLockers(res.data);
        } else {
          setLockers([]);
        }
      })
      .catch(() => {
        setLockers([]);
      })
      .finally(() => setLoading(false));
  };

  const getLockerLocations = () => {
    const user = tokenService.getUser();

    if (user.type === 'highadmin' || user.type === 'superadmin') {
      setLoading(true);
      Services.GetLockerLocations()
        .then((res) => {
          if (res.data) {
            const formattedData = res.data.map((item) => ({
              value: item.locationCode,
              name: item.name,
            }));
            setLocations(formattedData);
          } else {
            setLocations([]);
          }
        })
        .catch((err) => console.log(err))
        .finally(() => setLoading(false));
    } else {
      const allowedPoints = user.allowedPoints;
      let allowedCodes = [];

      if (allowedPoints && Object.keys(allowedPoints).length > 0) {
        Object.keys(allowedPoints).forEach((key) => {
          const point = allowedPoints[key];
          if (point.details && point.details.postboxesLocationCodes) {
            allowedCodes = allowedCodes.concat(
              point.details.postboxesLocationCodes
            );
          }
        });
      } else {
        setNoLocker(true);
        return;
      }
      allowedCodes = [...new Set(allowedCodes)];

      if (allowedCodes.length === 0) {
        setLocations([]);
        setNoLocker(true);
        return;
      }

      setLoading(true);
      Services.GetLockerLocations()
        .then((res) => {
          if (res.data) {
            const formattedData = res.data.map((item) => ({
              value: item.locationCode,
              name: item.name,
            }));

            const filteredData = formattedData.filter((location) =>
              allowedCodes.includes(location.value)
            );

            setLocations(filteredData);
            if (filteredData.length > 0) {
              setSelectedLocation(filteredData[0]);
              getLockers(filteredData[0].value);
            } else {
              setLoading(false);
              setNoLocker(true);
            }
          } else {
            setLoading(false);
            setLocations([]);
          }
        })
        .catch((err) => {
          setLoading(false);
          console.log(err);
        });
    }
  };

  useEffect(() => {
    getLockerLocations();
  }, []);

  return (
    <>
      <Helmet>
        <title>{language.locker_tracking_system.titles.site_title}</title>
      </Helmet>

      <Stack style={{ paddingInline: '25px' }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={5}
        >
          <Typography variant="h4" gutterBottom>
            {language.locker_tracking_system.titles.title}
          </Typography>
        </Stack>

        {noLocker ? (
          <Typography color="error">
            Bulunduğunuz lokasyona ait dolap bulunmamaktadır.
          </Typography>
        ) : (
          <>
            <Stack
              direction="row"
              justifyContent={isMobile ? 'space-between' : 'end'}
              mb={5}
              paddingX={2}
            >
              {isMobile ? (
                <FilterViewButton variant="contained" onClick={handleModal} />
              ) : null}
            </Stack>
            <Card>
              <CustomFilter
                modalType={'drawer'}
                filterModal={filterModal}
                handleCloseModal={handleCloseModal}
              >
                <>
                  <Stack
                    direction="column"
                    alignItems="center"
                    justifyContent="flex-start"
                    gap="15px"
                  >
                    <FormControl sx={{ flex: 1, minWidth: '100%' }}>
                      {locations.length === 0 ? (
                        <Typography color="error">
                          Bulunduğunuz lokasyona ait dolap bulunmamaktadır.
                        </Typography>
                      ) : (
                        <Select
                          displayEmpty
                          disabled={locations?.length === 1}
                          value={selectedLocation ? selectedLocation.name : ''}
                          renderValue={(selected) =>
                            selected ? (
                              selected
                            ) : (
                              <span style={{ color: '#aaa' }}>
                                Lokasyon Seç
                              </span>
                            )
                          }
                          onChange={(e) => {
                            const selected = locations.find(
                              (location) => location.name === e.target.value
                            );
                            getLockers(selected.value);
                            setSelectedLocation(selected);
                          }}
                        >
                          {locations.map((item, index) => (
                            <MenuItem key={index} value={item.name}>
                              {item.name}
                            </MenuItem>
                          ))}
                        </Select>
                      )}
                    </FormControl>
                  </Stack>
                </>
              </CustomFilter>
              {loading ? (
                <Backdrop
                  sx={{ color: '#fff', zIndex: 2, height: '100%' }}
                  open={loading}
                >
                  <CircularProgress color="inherit" />
                </Backdrop>
              ) : (
                <DynamicTable
                  data={lockers}
                  headers={TABLE_HEAD}
                  isOrdered={false}
                  isMobile={isMobile}
                  handleModal={handleModal}
                  handleCloseModal={handleCloseModal}
                  mustBeShown={selectedLocation ? selectedLocation.name : false}
                  initialOrder={'createdDateTime'}
                  initalOrderDirection={'desc'}
                />
              )}
            </Card>
          </>
        )}
      </Stack>

      {/* DETAY DİYALOĞU */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {language.locker_tracking_system.titles.title}
        </DialogTitle>
        <DialogContent>
          {selectedLocker && (
            <Stack spacing={2}>
              <Typography variant="subtitle1" color="text.secondary">
                {selectedLocation?.name || 'Seçili AVM Adı'}
              </Typography>

              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body1" fontWeight="bold">
                  Dolap No
                </Typography>
                <Typography
                  variant="body1"
                  color={
                    selectedLocker.statusName === 'Empty'
                      ? '#067647'
                      : '#B42318'
                  }
                >
                  {selectedLocker.lockerNumber}
                </Typography>
              </Stack>

              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body1">Başlangıç Tarihi</Typography>
                <Typography variant="body1">
                  {formatDate(selectedLocker.startDate)}
                </Typography>
              </Stack>

              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body1">Bitiş Tarihi</Typography>
                <Typography variant="body1">
                  {formatDate(selectedLocker.endDate)}
                </Typography>
              </Stack>

              {/* İsim Soyisim (Sadece InUse iken) */}
              {selectedLocker.statusName === 'InUse' && (
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body1">İsim Soyisim</Typography>
                  <Typography variant="body1">
                    {maskName(selectedLocker.fullName)}
                  </Typography>
                </Stack>
              )}

              {/* İletişim (InUse iken email öncelikli; email yoksa telefon) */}
              {selectedLocker.statusName === 'InUse' && (
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body1">İletişim</Typography>
                  <Typography variant="body1">
                    {getEmailFromLocker(selectedLocker) ||
                      (selectedLocker.gsm
                        ? formatGSM(selectedLocker.gsm)
                        : '-')}
                  </Typography>
                </Stack>
              )}

              {/* Şifre (InUse iken lockerPass yıldızlı) */}
              {selectedLocker.statusName === 'InUse' && (
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body1">Şifre</Typography>
                  <Typography variant="body1">
                    {maskLockerPass(selectedLocker.lockPass)}
                  </Typography>
                </Stack>
              )}

              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body1">Kullanım Süresi</Typography>
                <Typography variant="body1">
                  {selectedLocker.startDate && selectedLocker.endDate
                    ? calculateUsageTime(
                        selectedLocker.startDate,
                        selectedLocker.endDate
                      )
                    : '-'}
                </Typography>
              </Stack>

              <Button
                variant="outlined"
                fullWidth
                onClick={() => {
                  handlePasswordRequest();
                }}
              >
                Şifre Al
              </Button>
            </Stack>
          )}
        </DialogContent>
      </Dialog>

      {/* ŞİFRE DİYALOĞU */}
      <Dialog
        open={openPasswordDialog}
        onClose={handleClosePasswordDialog}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>
          <Stack>
            {/* Kapat Butonu */}
            <Button
              onClick={handleClosePasswordDialog}
              style={{
                position: 'absolute',
                top: '8px',
                right: '4px',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
              }}
            >
              <Iconify
                icon="mdi:close"
                style={{ fontSize: '20px', color: '#929191' }}
              />
            </Button>
            {/* Dekoratif Kilit */}
            <div
              style={{
                position: 'relative',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: '120px',
                height: '120px',
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  width: '120px',
                  height: '120px',
                  borderRadius: '50%',
                  border: '1px solid rgba(234, 236, 240, 0.7)',
                }}
              />
              <div
                style={{
                  position: 'absolute',
                  width: '180px',
                  height: '180px',
                  borderRadius: '50%',
                  border: '1px solid rgba(234, 236, 240, 0.5)',
                }}
              />
              <div
                style={{
                  position: 'absolute',
                  width: '240px',
                  height: '240px',
                  borderRadius: '50%',
                  border: '1px solid rgba(234, 236, 240, 0.3)',
                }}
              />
              <div
                style={{
                  position: 'absolute',
                  width: '300px',
                  height: '300px',
                  borderRadius: '50%',
                  border: '1px solid rgba(234, 236, 240, 0.1)',
                }}
              />
              <div
                style={{
                  position: 'relative',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  width: '80px',
                  height: '80px',
                  background: '#ECFDF3',
                  borderRadius: '50%',
                }}
              >
                <div
                  style={{
                    position: 'absolute',
                    width: '65px',
                    height: '65px',
                    background: '#ECFDF3',
                    borderRadius: '50%',
                  }}
                />
                <div
                  style={{
                    position: 'absolute',
                    width: '50px',
                    height: '50px',
                    background: '#DCFAE6',
                    borderRadius: '50%',
                  }}
                />
                <Iconify
                  icon="mdi:lock"
                  style={{
                    color: '#929191',
                    width: '28px',
                    height: '28px',
                    zIndex: 999,
                  }}
                />
              </div>
            </div>
          </Stack>
        </DialogTitle>
        <DialogContent style={{ zIndex: 1000 }}>
          <Stack spacing={3}>
            <Typography variant="h6" fontWeight="bold">
              Şifre Alma Talebi
            </Typography>
            <Typography variant="body1">
              <strong>{`"${selectedLocker?.lockerNumber}"`}</strong> Nolu{' '}
              {showPassword && password
                ? 'dolap için şifreniz: '
                : 'dolaba ait işlem için şifre almak istediğinize emin misiniz?'}
            </Typography>

            {showPassword && password && (
              <Typography
                style={{
                  fontSize: 24,
                  fontWeight: '700',
                  color: '#475467',
                  textAlign: 'center',
                }}
              >
                "{password}"
              </Typography>
            )}

            {!(password && showPassword) ? (
              <>
                <FormControlLabel
                  control={
                    <Checkbox
                      onChange={(e) => setIsChecked(e.target.checked)}
                    />
                  }
                  label="İşlemi onaylıyorum, bu işlem sonrası dolap durumu değişecektir."
                />
                <Stack direction="row" spacing={2} justifyContent="center">
                  <Button
                    variant="outlined"
                    onClick={handleClosePasswordDialog}
                    style={{
                      borderColor: '#EAECF0',
                      color: '#929191',
                      width: '100px',
                    }}
                  >
                    İptal Et
                  </Button>
                  <Button
                    variant="contained"
                    disabled={!isChecked || buttonLoading}
                    onClick={() => {
                      getPassword();
                    }}
                    style={{
                      backgroundColor: isChecked ? '#D92D20' : '#929191',
                      color: '#FFFFFF',
                      width: '100px',
                    }}
                  >
                    {buttonLoading ? (
                      <CircularProgress size={14} color="inherit" />
                    ) : (
                      'Devam Et'
                    )}
                  </Button>
                </Stack>
              </>
            ) : (
              <Button
                variant="contained"
                fullWidth
                onClick={handleClosePasswordDialog}
                style={{
                  backgroundColor: '#D92D20',
                  color: '#FFFFFF',
                }}
              >
                Kapat
              </Button>
            )}
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  );
}
